import { Extension, Prec, RangeSetBuilder } from '@codemirror/state';
import {
  Decoration,
  DecorationSet,
  EditorView,
  ViewPlugin,
  ViewUpdate,
} from '@codemirror/view';

const highlight = Decoration.mark({
  attributes: {
    style: 'animation: none',
  },
  class: 'cm-highlight',
});

/** Adds a class to every changed line compared to the previous prop state */
function highlightUpdatedLines(view: EditorView, previousValue: string) {
  const builder = new RangeSetBuilder<Decoration>();

  const previousSplittedValue = previousValue.split('\n');
  if (previousValue) {
    for (const { from, to } of view.visibleRanges) {
      for (let pos = from; pos <= to;) {
        const line = view.state.doc.lineAt(pos);
        if (previousSplittedValue[line.number - 1] !== line.text) {
          builder.add(line.from, line.to, highlight);
        }
        pos = line.to + 1;
      }
    }
  }
  return builder.finish();
}

/**
 * Extension for highlighting updated lines
 */
export const highlightExtension = (previousValue: string): Extension => {
  const highlightLines = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = highlightUpdatedLines(view, previousValue);

        // Needed to force a restart of the animation if (e.g.) a line has been modified
        requestAnimationFrame(() => {
          view?.dom.querySelectorAll('.cm-highlight').forEach((n) => {
            (n as HTMLDivElement).style.animation = '';
          });
        });
      }

      /** Run update if user changed input/focus */
      update(update: ViewUpdate) {
        if (update.docChanged || update.focusChanged) {
          this.decorations = new RangeSetBuilder<Decoration>().finish();
        }
      }
    },
    {
      decorations: (v) => v.decorations,
    },
  );
  return Prec.default(highlightLines);
};
